import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { WarehouseAPI } from '../services/api';
import {
  BundleCreationRequest,
} from '../types';

// Query Keys
export const queryKeys = {
  bundles: ['bundles'] as const,
  bundle: (id: string) => ['bundles', id] as const,
  inventory: ['inventory'] as const,
  inventoryBySKU: (skuId: string) => ['inventory', skuId] as const,
  stockValidation: (bundleSKUId: string, quantity: number) =>
    ['stock-validation', bundleSKUId, quantity] as const,
};

// Bundle SKU Hooks
export const useBundleSKUs = () => {
  return useQuery({
    queryKey: queryKeys.bundles,
    queryFn: WarehouseAPI.getBundleSKUs,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useBundleSKU = (id: string) => {
  return useQuery({
    queryKey: queryKeys.bundle(id),
    queryFn: () => WarehouseAPI.getBundleSKU(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

// Inventory Hooks
export const useInventoryReport = (skuIds?: string[]) => {
  return useQuery({
    queryKey: [...queryKeys.inventory, skuIds?.join(',')],
    queryFn: () => WarehouseAPI.getInventoryReport(skuIds),
    staleTime: 30 * 1000, // 30 seconds for inventory data
  });
};

export const useInventoryBySKU = (skuId: string) => {
  return useQuery({
    queryKey: queryKeys.inventoryBySKU(skuId),
    queryFn: () => WarehouseAPI.getInventoryBySKU(skuId),
    enabled: !!skuId,
    staleTime: 30 * 1000,
  });
};

// Bundle Search Hook (Now uses local bundle categories from MongoDB)
export const useBundleSearch = (searchTerm: string, page: number = 0, limit: number = 10, includeInactive: boolean = false) => {
  return useQuery({
    queryKey: ['bundle-search', searchTerm, page, limit, includeInactive],
    queryFn: () => WarehouseAPI.searchBundleCategories(searchTerm, page, limit, includeInactive),
    enabled: searchTerm.length >= 3, // Only search when user types 3+ characters
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
  });
};

export const useDebundleSuggestions = () => {
  return useQuery({
    queryKey: ['debundle-suggestions'],
    queryFn: () => WarehouseAPI.getDebundleSuggestions(),
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: false,
  });
};

// Shelf-wise Inventory Hooks
export const useShelfWiseInventory = (skuCode: string, page: number = 0, limit: number = 10) => {
  return useQuery({
    queryKey: ['shelf-inventory', skuCode, page, limit],
    queryFn: () => WarehouseAPI.getShelfWiseInventory(skuCode, page, limit),
    enabled: !!skuCode && skuCode.length >= 2, // Only search when SKU code is at least 2 characters
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
  });
};

// Upper bound on how many shelf-inventory rows we will pull in a single request to enable a TRUE
// full-dataset (cross-page) sort. Above this the viewer falls back to server-paged, page-scoped
// sorting — see ShelfInventoryViewer's fullMode. Keeps the one-shot fetch bounded and predictable.
export const SHELF_INVENTORY_SAFE_MAX = 1000;

// Full-dataset fetch used only when the result set is small enough (total <= SAFE_MAX) to sort
// across all pages client-side. Reuses getShelfWiseInventory with page 0 and a limit covering the
// whole set (the endpoint accepts a large limit; no signature change). `total` comes from the probe
// query so the one-shot limit is sized to the dataset, and the key includes it so a changed total
// refetches. Gated so it never fires for empty searches or oversized sets (avoids a fetch loop).
export const useShelfWiseInventoryAll = (skuCode: string, total: number, enabled: boolean) => {
  return useQuery({
    queryKey: ['shelf-inventory-all', skuCode, total],
    queryFn: () => WarehouseAPI.getShelfWiseInventory(skuCode, 0, Math.min(total || 0, SHELF_INVENTORY_SAFE_MAX)),
    enabled: enabled && !!skuCode && skuCode.length >= 2 && total > 0 && total <= SHELF_INVENTORY_SAFE_MAX,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
  });
};

export const useShelfInventoryById = (inventoryId: string) => {
  return useQuery({
    queryKey: ['shelf-inventory-item', inventoryId],
    queryFn: () => WarehouseAPI.getShelfInventoryById(inventoryId),
    enabled: !!inventoryId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

// Recent logs (activities) from local backend
export const useRecentActivities = (limit: number = 50, page: number = 1, type: string = 'all', q: string = '') => {
  return useQuery({
    queryKey: ['recent-activities', limit, page, type, q],
    queryFn: () => WarehouseAPI.getRecentLogs(limit, page, type, q),
    staleTime: 10 * 1000,
    refetchOnWindowFocus: false,
  });
};

// Stock Validation Hook
export const useStockValidation = (bundleSKUId: string, quantity: number) => {
  return useQuery({
    queryKey: queryKeys.stockValidation(bundleSKUId, quantity),
    queryFn: () => WarehouseAPI.validateStock(bundleSKUId, quantity),
    enabled: !!bundleSKUId && quantity > 0,
    staleTime: 10 * 1000, // 10 seconds
  });
};

// Mutation Hooks
export const useDeductChildSKUs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bundleSKUId, quantity, shelfLocations }: {
      bundleSKUId: string;
      quantity: number;
      shelfLocations: Record<string, string>;
    }) => WarehouseAPI.deductChildSKUs(bundleSKUId, quantity, shelfLocations),
    onSuccess: () => {
      // Invalidate inventory queries to refresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory });
    },
  });
};

export const useCreateBundle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: BundleCreationRequest) => WarehouseAPI.createBundle(request),
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory });
      queryClient.invalidateQueries({ queryKey: queryKeys.bundles });
    },
  });
};

export const useAddBundleToInventory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bundleSKUId, quantity, shelfLocation }: {
      bundleSKUId: string;
      quantity: number;
      shelfLocation: string;
    }) => WarehouseAPI.addBundleToInventory(bundleSKUId, quantity, shelfLocation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory });
    },
  });
};

export const useAdjustInventory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ skuId, quantity, operation, shelfLocation }: {
      skuId: string;
      quantity: number;
      operation: 'ADD' | 'SUBTRACT';
      shelfLocation?: string;
    }) => WarehouseAPI.adjustInventory(skuId, quantity, operation, shelfLocation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory });
    },
  });
};

// Logging Hooks (for local backend logs)
export const useLogChildDeduction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { sku: string; qty: number; bundleSku?: string; bundleQuantity?: number; shelfLocation?: string; metadata?: any }) =>
      WarehouseAPI.logChildDeduction(payload),
    onSuccess: () => {
      // no-op for now; keep in case we want to refresh inventory or activity logs
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory });
    }
  });
};

export const useLogBundleCreation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { bundleSku: string; quantity: number; childSkus?: Array<{ sku: string; qty: number }>; shelfLocation?: string; metadata?: any }) =>
      WarehouseAPI.logBundleCreation(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory });
      queryClient.invalidateQueries({ queryKey: queryKeys.bundles });
    }
  });
};